/**
 * AdminSidebarNav — Permanent left sidebar for the Super Admin console.
 *
 * Desktop-only (`lg:` and up). On smaller viewports the existing horizontal
 * TabsList takes over so the admin experience stays usable on tablet /
 * mobile without shipping a duplicate menu.
 *
 * Each item drives the same `activeTab` state the horizontal tabs use, so
 * we're not introducing a parallel routing system — the sidebar and the
 * hidden `TabsList` are two views of the same tab state.
 *
 * The active section is highlighted with TheBlkCommunity's gold accent per
 * the design language brief.
 */
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Store, Search, Trophy, Sparkles, Image as ImageIcon,
  ShoppingBag, Users, BarChart3, FileText, Settings, ArrowLeft, Crown,
} from "lucide-react";

// Sidebar → tab-value map. Every item maps to a REAL tab in AdminPage.jsx.
// `focus` optional param lets one sidebar item target a sub-area within
// its parent tab (e.g. "Search Businesses" jumps to biz-mgmt with the
// search input focused).
const NAV_ITEMS = [
  { label: "Dashboard",         tab: "dashboard",   icon: LayoutDashboard },
  { label: "Businesses",        tab: "biz-mgmt",    icon: Store },
  { label: "Search Businesses", tab: "biz-mgmt",    icon: Search, focus: "search" },
  { label: "Founding Wall",     tab: "founding",    icon: Trophy },
  { label: "Drops",             tab: "blinterest",  icon: Sparkles, focus: "drops" },
  { label: "Blinterest",        tab: "blinterest",  icon: ImageIcon },
  { label: "Orders",            tab: "orders",      icon: ShoppingBag },
  { label: "Users",             tab: "users",       icon: Users },
  { label: "Analytics",         tab: "traffic",     icon: BarChart3 },
  { label: "Content",           tab: "news-queue",  icon: FileText },
  { label: "Settings",          tab: "email",       icon: Settings },
];

export default function AdminSidebarNav({ activeTab, onSelect, focus }) {
  return (
    <aside
      className="hidden lg:flex flex-col w-60 flex-shrink-0 min-h-[calc(100vh-5rem)] bg-tbc-navy text-white pb-4"
      data-testid="admin-sidebar"
    >
      <div className="px-5 pt-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-tbc-gold" />
          <div>
            <p className="font-heading text-white text-base leading-none">Admin Dashboard</p>
            <p className="text-[10px] text-white/60 mt-1 tracking-wide uppercase">Manage. Grow. Empower.</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-3 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          // Highlight the sidebar item that produced the current view.
          // The pair (tab, focus) disambiguates "Businesses" from
          // "Search Businesses" — both target `biz-mgmt` but only one
          // carries the search-focus signal.
          const isActive =
            activeTab === item.tab &&
            (item.focus ? focus === item.focus : !focus);
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => onSelect(item.tab, item.focus)}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-tbc-gold/10 text-tbc-gold border-l-4 border-tbc-gold pl-4"
                  : "text-white/80 hover:text-white hover:bg-white/5 border-l-4 border-transparent"
              }`}
              data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="px-3 border-t border-white/10 pt-3">
        <Link
          to="/"
          className="w-full flex items-center gap-3 px-2 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 rounded-md transition-colors"
          data-testid="admin-sidebar-back-to-website"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Website</span>
        </Link>
      </div>
    </aside>
  );
}
